/**
 * Отправка телефона одним тапом из мини-аппа, с повтором при неудаче.
 *
 * До этого номер можно было подтвердить только кнопкой в чате бота —
 * одноразовой (`.oneTime()`), без видимого способа повторить, если сохранение
 * не удалось. `WebApp.requestContact()` показывает тот же попап, что и кнопка
 * в чате: сам номер в JS не приходит, Telegram шлёт `message.contact` в чат
 * бота, как обычно, и существующая проверка `contact.user_id === ctx.from.id`
 * не меняется. Меняется только то, что запросить это можно не выходя из
 * приложения — и запросить ещё раз, если не сработало.
 */
import { useEffect, useRef, useState } from 'react';
import type { Locale } from '@arbuz/shared';
import { useAuth } from '../auth';
import { makeT } from '../i18n';
import { canRequestContact, haptic, requestContact } from '../tg';

type Phase = 'idle' | 'checking' | 'declined' | 'timeout';

/** Сколько раз опросить сервер после согласия и с каким интервалом. */
const POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 1500;

export function SharePhoneButton({ locale }: { locale: Locale }) {
  const t = makeT(locale);
  const { state, refresh } = useAuth();
  const [phase, setPhase] = useState<Phase>('idle');
  // Не даёт стартовать второй опрос поверх уже идущего при повторных тапах
  const pollingRef = useRef(false);

  const hasPhone = state.status === 'ready' && state.user.has_phone;

  async function pollUntilConfirmed(): Promise<void> {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPhase('checking');

    for (let i = 0; i < POLL_ATTEMPTS; i += 1) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      await refresh();
      // `refresh()` обновляет общий контекст авторизации; эффект ниже сам
      // заметит `hasPhone === true` и переведёт `pollingRef` в false — этим
      // же флагом ловим и размонтирование компонента до завершения опроса.
      if (!pollingRef.current) return;
    }

    pollingRef.current = false;
    setPhase((p) => (p === 'checking' ? 'timeout' : p));
  }

  // Как только номер подтвердился — опрос можно остановить досрочно
  useEffect(() => {
    if (hasPhone) {
      pollingRef.current = false;
      setPhase('idle');
    }
  }, [hasPhone]);

  useEffect(
    () => () => {
      pollingRef.current = false;
    },
    [],
  );

  async function share(): Promise<void> {
    haptic('light');
    const shared = await requestContact();

    if (!shared) {
      setPhase('declined');
      return;
    }

    haptic('success');
    void pollUntilConfirmed();
  }

  if (hasPhone) return null;

  if (!canRequestContact()) {
    return (
      <p className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-xs)' }}>
        {t('sharePhoneUnsupported')}
      </p>
    );
  }

  return (
    <div style={{ marginTop: 'var(--s-3)' }}>
      <button
        type="button"
        className="btn-primary"
        style={{ width: '100%' }}
        disabled={phase === 'checking'}
        data-loading={phase === 'checking' ? 'true' : undefined}
        onClick={() => void share()}
      >
        {phase === 'checking' ? t('sharePhoneChecking') : t('sharePhoneButton')}
      </button>

      {phase === 'declined' && (
        <p role="status" className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-xs)' }}>
          {t('sharePhoneDeclined')}
        </p>
      )}

      {phase === 'timeout' && (
        <button
          type="button"
          onClick={() => void refresh()}
          style={{
            minHeight: 'var(--tap-min)',
            marginTop: 'var(--s-2)',
            background: 'none',
            border: 'none',
            color: 'var(--c-primary-text)',
            font: 'inherit',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {t('sharePhoneRetry')}
        </button>
      )}
    </div>
  );
}
